import { useEffect, useMemo, useState } from "react";
import Footer from "../../components/layouts/Footer";
import SortDropdown from "../../components/SortDropdown";
import SegmentedControl from "../../components/SegmentedControl";
import ArtworkCard from "../../components/ArtworkCard";
import CategoryChips from "../../components/CategoryChips";
import { ArtworkCardProps } from "../../types";
import { Link } from "react-router-dom";
import { usePersistStore, useStore } from "../../store";
import { AD_TYPES } from "../../utils/enums";
import { Skeleton } from "boneyard-js/react";
import { useFollowedAdsInfinite } from "../../hooks/queries";

const PAGE_SIZE = 9;
const TITLE = "Art from sellers you follow";

const mapAd = (tt: any): ArtworkCardProps => ({
  _id: tt?._id,
  color: true,
  imageUrl: tt?.images?.[0],
  category: tt?.categoryId?.name,
  subCategory: tt?.subCategoryId?.name,
  views: tt?.views || 0,
  title: tt?.title,
  ownerImage: tt?.userId?.fileUrl,
  ownerName: tt?.userId?.name,
  ownerId: tt?.userId?._id,
  biddingStarts: tt?.biddingStarts,
  biddingEnds: tt?.biddingEnds,
  price: tt?.price,
  showBidNowButton: true,
  showRemoveFavouriteButton: false,
  status: tt?.status,
  distance: tt?.distance,
});

function FollowedSellersAds() {
  const [sorting, setSorting] = useState<string>("recent-first");
  const [bidState, setBidState] = useState<string>(AD_TYPES.OPEN_FOR_BIDS);
  const { setErrorMessage } = useStore((s: any) => s);
  const { token, currentLocation } = usePersistStore((s: any) => s);

  const {
    data,
    isLoading,
    error: searchError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useFollowedAdsInfinite(token, {
    type: bidState,
    limit: PAGE_SIZE,
    sorting,
    location: currentLocation,
  });

  useEffect(() => {
    if (searchError) setErrorMessage((searchError as Error).message);
  }, [searchError, setErrorMessage]);

  const list: ArtworkCardProps[] = useMemo(
    () => (data?.pages || []).flatMap((p: any) => p?.ads || []).map(mapAd),
    [data]
  );
  const isReady = !isLoading;

  return (
    <>
      <div className="container px-md-0 px-3">
        <div className="row gx-md-0">
          <div className="col-md-12 px-md-0 px-3 mt-md-4 mt-3">
            <CategoryChips />
          </div>
          <div className="breadCrumb mt-4 mb-3">
            <div className="d-flex gap-3">
              <Link to="/">
                <p className="m-0">Home</p>
              </Link>
              <span>/</span>
              <Link to="">
                <h4>{TITLE}</h4>
              </Link>
            </div>
          </div>
          <div className="col-md-12 pb-4">
            <div className="d-flex align-items-center customTitle pb-3 px-0 justify-content-between flex-wrap gap-2">
              <h2 className="text-center">{TITLE}</h2>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <SegmentedControl
                  ariaLabel="Filter by bid state"
                  value={bidState}
                  onChange={setBidState}
                  options={[
                    { value: AD_TYPES.OPEN_FOR_BIDS, label: "Open for bids" },
                    { value: AD_TYPES.UPCOMING, label: "Upcoming" },
                    { value: AD_TYPES.SOLD, label: "Sold" },
                  ]}
                />
                <SortDropdown
                  value={sorting}
                  onChange={setSorting}
                  options={[
                    { value: "recent-first", label: "Newest first" },
                    { value: "price-low-to-high", label: "Price low to high" },
                    { value: "price-high-to-low", label: "Price high to low" },
                  ]}
                />
              </div>
            </div>
            <div className="row">
              {!isReady &&
                Array.from({ length: PAGE_SIZE }).map((_, i) => (
                  <div className="col-12 col-sm-6 col-md-4 mb-5" key={`sk-${i}`}>
                    <Skeleton name="artwork-card" loading={true}>
                      <span style={{ display: "block", aspectRatio: "3 / 4" }} />
                    </Skeleton>
                  </div>
                ))}
              {isReady && list?.length === 0 && (
                <div className="col-12 text-center">
                  No ads from the sellers you follow yet.
                </div>
              )}
              {isReady &&
                list?.map((slide: ArtworkCardProps, index: number) => (
                  <div className="col-12 col-sm-6 col-md-4 mb-5" key={index}>
                    <ArtworkCard {...slide} />
                  </div>
                ))}
            </div>
            {isReady && list?.length > 0 && hasNextPage && (
              <button
                className="mainBtn px-5 mx-auto my-4"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                style={{ opacity: isFetchingNextPage ? 0.7 : 1 }}
              >
                {isFetchingNextPage ? "Loading…" : "Load More"}
              </button>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default FollowedSellersAds;
